/// <reference path="../../../node_modules/react-datetime/react-datetime.d.ts" />

import * as React from 'react';
import * as Site from '../../shared/Site';
import { Button, Modal, FormGroup, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { ICalendarItem, ICalendarSettings, ICalendarItemUserType } from '../calendar/Types';
import { Translate } from '../../shared/LanguageService';
import { IWorkingHour } from '../calendar/IWorkingHour';
import { Calendar } from '../calendar/Calendar';
import { toMinMaxTime } from '../calendar/calendarTimeUtils';
import { assign } from '../../shared/Assign';
import { IStylingUserCalendar } from './Types';
import RadioInputSwitch from '../../components/common/RadioInputSwitch';
import ICalendar from '../calendar/ICalendar';
import WorkingHoursForm from './WorkingHoursForm';
import StylingUserCalendarPrintEvents from './StylingUserCalendarPrintEvents';
import { Right } from 'react-bootstrap/lib/Media';
import moment from 'moment';

interface IState {
    calendar: IStylingUserCalendar;
    calendarSettings: ICalendarSettings;
    showModal: boolean;
    showPrintEventModal: boolean;
    showSe360WorkingHours: boolean;
}

interface IProps {
    calendar: any;
    calendarSettings?: {};
    onChange?: (calendar: ICalendar) => void;
    onCreated?: (event: any) => void;
    onUpdated?: (event: any) => void;
    onDeleted?: (event: any) => void;
    hideEditWorkingHours?: boolean;
    showSe360WorkingHours?: boolean;
    onRangeChanged?: (from: any, to: any) => void;
    dontUseMinMaxTime?: boolean;
    user: any;
}

class StylingUserCalendarComponent extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.onEventsChange = this.onEventsChange.bind(this);

        let businessHours: any = [0, 1, 2, 3, 4, 5, 6].map((day) => {
            return {
                day: day,
                isAvailable: !(day === 0 || day === 6),
                start: moment('08:00:00', 'HH:mm:ss').toDate(),
                end: moment('17:00:00', 'HH:mm:ss').toDate()
            };
        });
        let calendarSettings = this.getCalendarSettings([], businessHours);
        this.state = {
            calendar: {
                workingHours: [],
                items: [],
                photographerIds: [],
                se360WorkingHours: businessHours,
                scandphotosWorkingHours: businessHours
            },
            calendarSettings: calendarSettings,
            showModal: false,
            showPrintEventModal: false,
            showSe360WorkingHours: true
        };

        this.onChange = this.onChange.bind(this);
        this.onEventCreated = this.onEventCreated.bind(this);
        this.onEventUpdated = this.onEventUpdated.bind(this);
        this.onEventDeleted = this.onEventDeleted.bind(this);
        this.workHourDateChange = this.workHourDateChange.bind(this);
        this.showPrintEventModal = this.showPrintEventModal.bind(this);
        this.hidePrintEventModal = this.hidePrintEventModal.bind(this);
    }

    getCalendarSettings(events: any[], workingHours: Array<IWorkingHour>): ICalendarSettings {
        let businessHours = [];

        if (workingHours && workingHours.length > 0) {
            businessHours = workingHours.map((w) => {
                return {
                    dow: [w.day],
                    start: w.isAvailable ? this.dateToTime(w.start) : '',
                    end: w.isAvailable ? this.dateToTime(w.end) : '',
                    isAvailable: w.isAvailable
                };
            });
        } else if (this.state && this.state.calendar && this.state.calendar.se360WorkingHours) {
            businessHours = this.state.calendar.se360WorkingHours;
            businessHours = businessHours.map((w) => {
                return {
                    dow: [w.day],
                    start: w.isAvailable ? this.dateToTime(w.start) : '',
                    end: w.isAvailable ? this.dateToTime(w.end) : '',
                    isAvailable: w.isAvailable
                };
            });
        }

        let calendarSettings: any = {
            selectable: true,
            eventSources: [
                {
                    events: events
                }
            ],
            eventRender: (event, element) => {
                element.append(
                    `<div style="font-size:10px;font-weight: bold;">` +
                        `<span style="font-size:11px">${
                            event.description ? event.description : ''
                        }</span>` +
                        `</br>` +
                        `<span>${event.product ? event.product : ''}</span>` +
                        `</div>`
                );
            },
            eventConstraint: 'businessHours',
            selectConstraint: 'businessHours',
            selectHelper: true,
            slotLabelFormat: 'HH:mm',
            timeFormat: 'HH:mm',
            firstDay: 1,
            header: {
                left: 'prev, next',
                center: 'title',
                right: 'agendaWeek, month'
            },
            defaultView: 'agendaWeek'
        };

        if (!this.props.dontUseMinMaxTime) {
            const getBusinessHour = (time: string) =>
                time ? parseInt(time.split(':')[0]) : undefined;
            const getMax = (hour: number, previousHour?: number) =>
                Math.max(...[hour === 0 ? 24 : hour + 1, previousHour]);
            const getMin = (hour: number, previousHour?: number) =>
                Math.min(...[hour <= 0 ? 0 : hour - 1, previousHour]);
            let firstAvailableBusinessHour = (businessHours || []).find((x) => x.isAvailable);
            let minTime = firstAvailableBusinessHour
                ? getBusinessHour(firstAvailableBusinessHour.start)
                : 7;
            let maxTime = firstAvailableBusinessHour
                ? getBusinessHour(firstAvailableBusinessHour.end)
                : 18;

            businessHours
                .filter((x) => x.isAvailable)
                .forEach((x) => {
                    minTime = getMin(getBusinessHour(x.start), minTime);
                    maxTime = getMax(getBusinessHour(x.end), maxTime);
                });

            events.forEach((event) => {
                // +/- 1 to get a margin
                const eventStartHour = moment(event.start).hours();
                const eventEndHour = moment(event.end).hours();

                minTime = getMin(eventStartHour, minTime);
                maxTime = getMax(eventEndHour, maxTime);

                if (event.originalStart) {
                    event.start = event.originalStart;
                }

                if (event.originalEnd) {
                    event.end = event.originalEnd;
                }
            });

            const minMaxTime = toMinMaxTime(minTime, maxTime);
            calendarSettings.minTime = minMaxTime.minTime;
            calendarSettings.maxTime = minMaxTime.maxTime;
        }

        if (businessHours.length > 0 || businessHours[0]) {
            calendarSettings.businessHours = businessHours;
        }

        if (this.props.calendarSettings) {
            calendarSettings = assign(calendarSettings, this.props.calendarSettings);
        }

        return calendarSettings;
    }

    componentDidMount() {
        if (
            this.props.calendar &&
            ((this.props.calendar.hasOwnProperty('items') &&
                this.props.calendar.hasOwnProperty('se360WorkingHours') &&
                this.props.calendar.hasOwnProperty('scandphotosWorkingHours')) ||
                (this.props.calendar.eventSources && this.props.calendar.eventSources.length > 0))
        ) {
            this.init(assign({}, this.props.calendar));
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: IProps) {
        if (
            nextProps.calendar &&
            nextProps.calendar.hasOwnProperty('items') &&
            nextProps.calendar.hasOwnProperty('se360WorkingHours') &&
            nextProps.calendar.hasOwnProperty('scandphotosWorkingHours')
        ) {
            this.init(assign({}, nextProps.calendar));
        }

        if (nextProps.showSe360WorkingHours !== undefined) {
            this.setState({
                showSe360WorkingHours: nextProps.showSe360WorkingHours
            } as IState);
        }
    }

    init(calendar: any) {
        calendar.items &&
            calendar.items.map((x) => {
                if (x.site === Site.SE360) {
                    x.className = ['se360'];
                }
            });

        if (calendar.items) {
        } else {
            calendar.items = assign([], calendar.eventSources[0].events);
            calendar.eventSources = [];
        }

        if (calendar.se360WorkingHours) {
        } else {
            calendar.se360WorkingHours = calendar.workingHours;
        }

        let calendarSettings = this.getCalendarSettings(
            calendar.items ? calendar.items : [],
            calendar.se360WorkingHours
        );

        if (calendar.se360WorkingHours && calendar.se360WorkingHours.length === 0) {
            calendar.se360WorkingHours = [0, 1, 2, 3, 4, 5, 6].map((day) => {
                return {
                    id: null,
                    photographerId: null,
                    day: day,
                    isAvailable: !(day === 0 || day === 6),
                    start: moment('08:00:00', 'HH:mm:ss').format(),
                    end: moment('17:00:00', 'HH:mm:ss').format()
                };
            });
        }

        const newCalendar = {
            items: calendar.items,
            se360WorkingHours: calendar.se360WorkingHours
        };

        this.setState({
            calendarSettings: calendarSettings,
            calendar: newCalendar
        } as IState);
    }

    showModal() {
        this.setState({ showModal: true } as IState);
    }

    showPrintEventModal() {
        this.setState({ showPrintEventModal: true } as IState);
    }

    dateToTime(str: string): string {
        let d = null;

        if (moment.isMoment(str)) {
            d = moment(str).toDate();
        } else {
            d = new Date(str);
        }

        if (!isNaN(d.getTime())) {
            let hhmmss = d.toTimeString().split(' ')[0];
            let temp = hhmmss.split(':');
            return `${temp[0]}:${temp[1]}`;
        }
        return str;
    }

    hideModal() {
        this.setState({ showModal: false } as IState);
    }

    hidePrintEventModal() {
        this.setState({ showPrintEventModal: false } as IState);
    }

    saveBusinessHours() {
        this.hideModal();
    }

    onChange(calendar: IStylingUserCalendar) {
        if (this.props.onChange) {
            this.props.onChange(calendar);
        }
    }

    onEventCreated(event: any) {
        event.photographerId = this.props.calendar.photographerId;
        if (this.props.onCreated) {
            this.props.onCreated(event);
        }
    }
    onEventUpdated(event: any) {
        if (this.props.onUpdated) {
            this.props.onUpdated(event);
        }
    }
    onEventDeleted(event: any) {
        if (this.props.onDeleted) {
            this.props.onDeleted(event);
        }
    }

    onEventsChange(events: ICalendarItem[]) {
        let calendar: IStylingUserCalendar = assign({}, this.state.calendar);
        calendar.items = assign([], events);
        this.setState({ calendar: calendar } as IState);

        this.onChange(calendar);
    }

    workHourDateChange(workingHours: any[], isSe360: boolean) {
        let calendar = assign({}, this.state.calendar);

        if (isSe360) {
            calendar.se360WorkingHours = assign([], workingHours);
        } else {
            calendar.scandphotosWorkingHours = assign([], workingHours);
        }

        this.onChange(calendar);
    }

    cloneWorkingHoursArray(workingHoursList: any[]) {
        let workingHours = [];
        let sunday: any = null;
        for (let i = 0; i < workingHoursList.length; i++) {
            let workingHour = workingHoursList[i];
            let wh = {
                day: workingHour.day,
                start: moment(workingHour.start),
                end: moment(workingHour.end),
                isAvailable: workingHour.isAvailable
            };
            if (workingHour.day === 0) {
                sunday = wh;
            } else {
                workingHours.push(wh);
            }
        }

        workingHours = workingHours.sort((a, b) => {
            if (b.day === 0) return -1;
            return a.day > b.day ? 1 : a.day === b.day ? 0 : -1;
        });

        if (sunday !== null) {
            workingHours.push(sunday);
        }

        return workingHours;
    }

    render() {
        let se360WorkingHours = this.cloneWorkingHoursArray(this.state.calendar.se360WorkingHours);
        return (
            <div>
                {this.props.hideEditWorkingHours !== true && (
                    <Modal
                        show={this.state.showModal}
                        onHide={() => {
                            this.hideModal();
                        }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {Translate('photographer/modal/form/calendar/edit-working-hours')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Tabs defaultActiveKey={1} id="working-hours-uncontrolled-tab">
                                <Tab eventKey={1} title="SE360">
                                    <WorkingHoursForm
                                        workingHours={se360WorkingHours}
                                        onChange={(workingHours: any[]) =>
                                            this.workHourDateChange(workingHours, true)
                                        }
                                    />
                                </Tab>
                            </Tabs>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={() => {
                                    this.saveBusinessHours();
                                }}
                            >
                                {Translate('photographers/modal/footer/button-label/close')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
                {this.props.hideEditWorkingHours !== true && this.state.showPrintEventModal && (
                    <div>
                        <StylingUserCalendarPrintEvents
                            show={this.state.showPrintEventModal}
                            hide={() => {
                                this.hidePrintEventModal();
                            }}
                            calendarItems={this.state.calendar.items}
                        />
                    </div>
                )}
                <Row>
                    <Col md={12} className="photographer-calendar-toolbar">
                        {
                            //Inte aktivt än. Men skall läggas till.
                            //this.props.hideEditWorkingHours !== true &&
                            //<Button bsStyle="info" onClick={() => { this.showModal(); }}>{Translate('photographer/modal/form/calendar/edit-working-hours')}</Button>
                        }

                        {
                            //Inte aktivt än. Men skall läggas till.
                            //this.props.showSe360WorkingHours === undefined &&
                            //<FormGroup className="" style={{ float: "right" }} >
                            //    <Button className="" onClick={() => this.setState({ showPrintEventModal: true } as IState)}>{Translate('photographer/modal/form/calendar/print')}</Button>
                            //</FormGroup>
                        }
                    </Col>
                </Row>
                <Calendar
                    calendarItemUserType={ICalendarItemUserType.StylingUser}
                    settings={this.state.calendarSettings}
                    calendarItems={this.state.calendar ? this.state.calendar.items : null}
                    workingHours={this.state.calendar.se360WorkingHours}
                    onChange={this.onEventsChange}
                    onUpdated={this.onEventUpdated}
                    onCreated={this.onEventCreated}
                    onDeleted={this.onEventDeleted}
                    onRangeChanged={this.props.onRangeChanged}
                    user={this.props.user}
                />
            </div>
        );
    }
}

export default StylingUserCalendarComponent;
